import React, { Fragment } from 'react';
import { arrayOf, number, oneOf, shape, string } from 'prop-types';

import Figure from 'components/Figure';
import placementConfigs from 'components/ImagePlaceholder/configs';
import SaveToWishListCSR from 'components/SaveToWishList/SaveToWishListCSR';
import { POST_COL_WIDTH } from 'constants/layout';
import { MQ_LARGE, MQ_MEDIUM, MQ_MIDLARGE, MQ_SMALL_ONLY } from 'constants/mq';
import { HAS_WISH_LISTS } from 'constants/site';
import { getAffiliateLinkProps } from 'util/affiliates/amazon';
import styles from './styles';

const SPONSORED = 'sponsored';

export const DEFAULT_IMAGE_DIM = 365;

const DEFAULT_SOURCE_SIZES = [
  {
    media: MQ_MIDLARGE,
    layoutWidth: {
      value: 730,
      units: 'px'
    }
  },
  {
    media: MQ_LARGE,
    layoutWidth: {
      value: 610,
      units: 'px'
    }
  },
  {
    media: MQ_MEDIUM,
    layoutWidth: {
      value: 696,
      units: 'px'
    }
  },
  {
    media: MQ_SMALL_ONLY,
    layoutWidth: {
      value: 343,
      units: 'px'
    }
  },
  {
    layoutWidth: {
      value: 100,
      units: 'vw'
    }
  }
];

function ProductTeaserListItem(props) {
  const {
    description,
    humanReadableId,
    image,
    imageUrl,
    name,
    num,
    placement,
    price,
    retailerName,
    salePrice,
    sourceSizes,
    subtitle,
    tags,
    total,
    url,
    variant
  } = props;

  if (!name && !url) {
    return null;
  }

  const isSponsored = tags && tags.map(t => t.slug).includes(SPONSORED);
  const linkProps = getAffiliateLinkProps(url);
  const tagsString = (tags && tags.map(tag => tag.slug).join(',')) || '';

  return (
    <div className={`ProductTeaserListItem ProductTeaserListItem--${variant}`}>
      {imageUrl && (
        <Fragment>
          <a
            className="ProductTeaserListItem__image"
            data-gtm-product-block="teaser-list"
            data-gtm-product-id={humanReadableId}
            data-gtm-product-retailers={retailerName}
            data-gtm-product-tags={tagsString}
            data-gtm-trigger="product-buy-link-click"
            data-test="affiliateLink"
            rel="nofollow"
            target="_blank"
            {...linkProps}
            aria-label={`Picture of ${name}`}
          >
            <Figure
              alt={`Product Image: ${name}`}
              hideImageActions
              isPinnable={false}
              maxWidth={POST_COL_WIDTH}
              originalHeight={
                (image && image.originalHeight) || DEFAULT_IMAGE_DIM
              }
              originalWidth={
                (image && image.originalWidth) || DEFAULT_IMAGE_DIM
              }
              placement={placement}
              sourceSizes={sourceSizes}
              url={imageUrl}
            />
          </a>
          <div className="ProductTeaserListItem__meta ProductTeaserListItem__counter">
            {num} / {total}
          </div>
          {isSponsored && (
            <div className="ProductTeaserListItem__meta ProductTeaserListItem__cake">
              Partner Pick
            </div>
          )}
        </Fragment>
      )}
      <div className="ProductTeaserListItem__body">
        {subtitle && (
          <div
            className="ProductTeaserListItem__subtitle"
            data-test="productSubtitle"
          >
            {subtitle}
          </div>
        )}
        <div className="ProductTeaserListItem__name">
          <a
            data-gtm-product-block="teaser-list"
            data-gtm-product-id={humanReadableId}
            data-gtm-product-retailers={retailerName}
            data-gtm-product-tags={tagsString}
            data-gtm-trigger="product-buy-link-click"
            data-test="affiliateLink"
            rel="nofollow"
            target="_blank"
            {...linkProps}
          >
            {name}
          </a>
        </div>
        {retailerName && (
          <div className="ProductTeaserListItem__retailer">{retailerName}</div>
        )}
        {(salePrice > 0 || price > 0) && (
          <div className="ProductTeaserListItem__price">
            ${((salePrice || price) / 100).toFixed(2)}
          </div>
        )}
        {price > 0 && salePrice > 0 && (
          <div className="ProductTeaserListItem__comparisonPrice">
            was ${(price / 100).toFixed(2)}
          </div>
        )}
        {description && (
          <div
            className="ProductTeaserListItem__description"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </div>

      <div className="ProductTeaserListItem__actions">
        <a
          data-gtm-product-block="teaser-list"
          data-gtm-product-id={humanReadableId}
          data-gtm-product-retailers={retailerName}
          data-gtm-product-tags={tagsString}
          data-gtm-trigger="product-buy-link-click"
          data-test="affiliateLink"
          rel="nofollow"
          target="_blank"
          {...linkProps}
        >
          <div className="ProductTeaserListItem__cta" aria-label="buy now">
            Buy Now
          </div>
        </a>
        {HAS_WISH_LISTS && (
          <span className="ProductTeaserListItem__wishlistCta">
            <SaveToWishListCSR
              product={{
                id: humanReadableId,
                imageUrl,
                name,
                retailerName,
                tags,
                url
              }}
              source="teaser-list"
            />
          </span>
        )}
      </div>
      <style jsx>{styles}</style>
    </div>
  );
}

ProductTeaserListItem.defaultProps = {
  description: undefined,
  image: undefined,
  imageUrl: undefined,
  num: undefined,
  placement: 'inPost',
  price: undefined,
  retailerName: undefined,
  salePrice: undefined,
  sourceSizes: DEFAULT_SOURCE_SIZES,
  subtitle: undefined,
  tags: undefined,
  total: undefined,
  variant: 'post'
};

ProductTeaserListItem.propTypes = {
  description: string,
  humanReadableId: string.isRequired,
  image: shape({
    originalHeight: number,
    originalWidth: number
  }),
  imageUrl: string,
  name: string.isRequired,
  num: number,
  placement: oneOf(Object.keys(placementConfigs)),
  price: number,
  retailerName: string,
  salePrice: number,
  sourceSizes: arrayOf(
    shape({
      layoutWidth: shape({ units: string, value: number }),
      media: string
    })
  ),
  subtitle: string,
  tags: arrayOf(
    shape({
      slug: string.isRequired
    })
  ),
  total: number,
  url: string.isRequired,
  variant: oneOf(['post', 'wishList'])
};

export default ProductTeaserListItem;
